import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import {
  ArrowUpDown, Users, Search, TrendingUp, Target,
  MapPin, DollarSign, Eye, Award, AlertTriangle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';
import { intelligenceAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

// Move SortIcon outside the component to avoid re-render issues
const SortIcon = ({ field, sortField }) => (
  <ArrowUpDown className={`w-3 h-3 ml-1 ${sortField === field ? 'text-teal-700' : 'text-slate-300'}`} />
);

const StaffInsightsPage = () => {
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [sortField, setSortField] = useState('score_overall');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (activeBusiness?.id) {
      fetchStaffList();
    }
  }, [activeBusiness?.id]);

  const fetchStaffList = async () => {
    try {
      setLoading(true);
      const { data } = await intelligenceAPI.getStaffList();
      setStaffList(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load staff insights'));
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedStaff = [...staffList].sort((a, b) => {
    const aVal = a[sortField] || 0;
    const bVal = b[sortField] || 0;
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const filteredStaff = sortedStaff.filter(staff =>
    !searchQuery ||
    staff.staff_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getInitialColor = (name) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-purple-100 text-purple-700',
      'bg-amber-100 text-amber-700',
      'bg-rose-100 text-rose-700',
      'bg-cyan-100 text-cyan-700',
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-emerald-50 text-emerald-700';
    if (score >= 60) return 'bg-blue-50 text-blue-700';
    if (score >= 40) return 'bg-amber-50 text-amber-700';
    return 'bg-rose-50 text-rose-700';
  };

  const getPerformanceLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Average';
    return 'Needs Improvement';
  };

  const avgScore = staffList.length > 0
    ? (staffList.reduce((sum, s) => sum + (s.score_overall || 0), 0) / staffList.length).toFixed(0)
    : 0;
  const totalVisits = staffList.reduce((sum, s) => sum + (s.total_visits_completed || 0), 0);
  const totalCollections = staffList.reduce((sum, s) => sum + (s.total_collections_amount || 0), 0);

  return (
    <div className="h-full flex flex-col" data-testid="staff-insights-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Staff Insights"
        subtitle="Performance metrics and analytics for all field staff"
        stats={[
          { icon: Users, label: 'Total Staff', value: staffList.length, color: 'slate' },
          { icon: Award, label: 'Avg. Score', value: avgScore, color: 'emerald' },
          { icon: Target, label: 'Total Visits', value: totalVisits, color: 'blue' },
          { icon: DollarSign, label: 'Total Collections', value: formatCurrency(totalCollections), color: 'purple' },
        ]}
        primaryAction={{
          label: 'Manage Staff',
          icon: Users,
          onClick: () => navigate(`/business/${activeBusiness?.id}/staff`)
        }}
        secondaryAction={{
          label: 'Overview',
          icon: Eye,
          onClick: () => navigate(`/business/${activeBusiness?.id}/intelligence/overview`)
        }}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search staff by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading staff insights...</p>
              </div>
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No staff found</p>
              <p className="text-[13px] text-slate-400 mt-1">No staff members match your search</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table table-fixed w-full">
                  <colgroup>
                    <col style={{ width: '24%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '16%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal sticky top-0 z-10 bg-white">Staff Member</th>
                      <th className="tl-th tl-th-teal sticky top-0 z-10 bg-white">Performance</th>
                      <th
                        className="tl-th tl-th-teal sticky top-0 z-10 bg-white cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('score_overall')}
                      >
                        <div className="flex items-center">Score<SortIcon field="score_overall" sortField={sortField} /></div>
                      </th>
                      <th
                        className="tl-th tl-th-teal sticky top-0 z-10 bg-white cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('total_visits_completed')}
                      >
                        <div className="flex items-center">Visits<SortIcon field="total_visits_completed" sortField={sortField} /></div>
                      </th>
                      <th
                        className="tl-th tl-th-teal sticky top-0 z-10 bg-white cursor-pointer hover:bg-slate-100"
                        onClick={() => handleSort('total_distance_km')}
                      >
                        <div className="flex items-center">Distance<SortIcon field="total_distance_km" sortField={sortField} /></div>
                      </th>
                      <th
                        className="tl-th tl-th-teal sticky top-0 z-10 bg-white cursor-pointer hover:bg-slate-100 text-right"
                        onClick={() => handleSort('total_collections_amount')}
                      >
                        <div className="flex items-center justify-end">Collections<SortIcon field="total_collections_amount" sortField={sortField} /></div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="animate-stagger">
                    {filteredStaff.map((staff, idx) => (
                      <tr
                        key={staff.staff_id}
                        className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn cursor-pointer ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                        style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                        onClick={() => navigate(`/business/${activeBusiness?.id}/intelligence/staff/${staff.staff_id}`)}
                      >
                        <td className="tl-td">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-semibold ${getInitialColor(staff.staff_name)}`}>
                              {getInitials(staff.staff_name)}
                            </div>
                            <div>
                              <p className="text-[13px] font-semibold text-slate-800">{staff.staff_name}</p>
                              <p className="text-[11px] text-slate-500">Rank #{idx + 1}</p>
                            </div>
                          </div>
                        </td>
                        <td className="tl-td">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium ${getScoreColor(staff.score_overall)}`}>
                            {staff.score_overall >= 80 ? <TrendingUp className="w-3 h-3" /> :
                             staff.score_overall < 40 ? <AlertTriangle className="w-3 h-3" /> : null}
                            {getPerformanceLabel(staff.score_overall)}
                          </span>
                        </td>
                        <td className="tl-td">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${getScoreColor(staff.score_overall)}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              staff.score_overall >= 80 ? 'bg-emerald-500' :
                              staff.score_overall >= 60 ? 'bg-blue-500' :
                              staff.score_overall >= 40 ? 'bg-amber-500' : 'bg-rose-500'
                            }`}></span>
                            {staff.score_overall?.toFixed(0) || 0}
                          </span>
                        </td>
                        <td className="tl-td">
                          <div className="text-[13px] font-semibold text-slate-800">
                            {staff.total_visits_completed || 0}/{staff.total_visits_planned || 0}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {staff.total_visits_planned > 0
                              ? `${Math.round((staff.total_visits_completed / staff.total_visits_planned) * 100)}% complete`
                              : 'No visits planned'}
                          </div>
                        </td>
                        <td className="tl-td">
                          <div className="flex items-center gap-1.5 text-[13px] text-slate-600">
                            <MapPin className="w-3.5 h-3.5 text-slate-400" />
                            {staff.total_distance_km?.toFixed(1) || 0} km
                          </div>
                        </td>
                        <td className="tl-td text-right">
                          <div className="flex items-center justify-end gap-1.5 text-[13px] font-semibold text-slate-800">
                            <DollarSign className="w-3.5 h-3.5 text-slate-400" />
                            {formatCurrency(staff.total_collections_amount)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredStaff.length}</span> of <span className="font-medium text-slate-700">{staffList.length}</span> staff members
                </p>
                <p className="text-[11px] text-slate-400">Click on a row to view detailed performance</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffInsightsPage;
